import { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2, ChevronRight, Award, Flag, ArrowRight, BarChart2, BookOpen, Clock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/ui/card";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LineChart,
  Line,
} from "recharts";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { loadQuestionsFromDB } from "@/services/aiQuestionService";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { useUserRole } from "@/hooks/useUserRole";
import { ChildPerformanceCard } from "@/components/parent/ChildPerformanceCard";

const Analysis = () => {
  const { grade, subject, category } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [analysisData, setAnalysisData] = useState<any>(null);
  const [subjects, setSubjects] = useState<any[]>([]);
  const [selectedSubject, setSelectedSubject] = useState<string | null>(null);
  const [subjectData, setSubjectData] = useState<any>(null);
  const [selectedView, setSelectedView] = useState<"overview" | "subjects">("overview");
  const [sortedSubjects, setSortedSubjects] = useState<any[]>([]);

  const { role, loading: roleLoading } = useUserRole();
  type ParentChild = { id: string; student_email: string; student_name: string };
  const [parentChildren, setParentChildren] = useState<ParentChild[]>([]);
  const [parentLoading, setParentLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }
    if (role === 'parent') {
      // Parent analysis handled separately
      setLoading(false);
      return;
    }
    fetchAnalysisData();
  }, [user, role]);

  useEffect(() => {
    const loadChildren = async () => {
      if (!user || role !== 'parent') return;
      try {
        setParentLoading(true);
        const { data: parentData } = await supabase
          .from('parents')
          .select('id')
          .eq('user_id', user.id)
          .single();
        if (parentData) {
          const { data: childrenData } = await supabase
            .from('children')
            .select('id, student_email, student_name')
            .eq('parent_id', parentData.id)
            .order('created_at', { ascending: false });
          setParentChildren(childrenData || []);
        } else {
          setParentChildren([]);
        }
      } catch (e) {
        console.error('Error loading children for analysis:', e);
        setParentChildren([]);
      } finally {
        setParentLoading(false);
      }
    };
    loadChildren();
  }, [user, role]);

  useEffect(() => {
    if (subjects.length > 0 && !selectedSubject) {
      setSelectedSubject(subjects[0].id);
    }
  }, [subjects]);

  useEffect(() => {
    if (selectedSubject && analysisData) {
      generateSubjectData(selectedSubject);
    }
  }, [selectedSubject, analysisData]);

  useEffect(() => {
    if (analysisData) {
      // Generate a sorted list of subjects by performance
      const sorted = Object.entries(analysisData).map(([id, data]: [string, any]) => ({
        id,
        name: data.name,
        grade: data.grade,
        score: data.overallScore,
        questionsAnswered: data.totalQuestions
      }))
      .sort((a, b) => b.score - a.score);
      
      setSortedSubjects(sorted);
    }
  }, [analysisData]);

  const fetchAnalysisData = async () => {
    try {
      setLoading(true);

      const { data: sessionsData, error: sessionsError } = await supabase
        .from("test_sessions")
        .select(`
          id, 
          grade, 
          score, 
          total_questions, 
          time_taken,
          created_at,
          subjects(id, name)
        `)
        .eq("user_id", user!.id)
        .eq("completed", true)
        .order("created_at", { ascending: false });

      if (sessionsError) {
        console.error("Error fetching test sessions:", sessionsError);
        toast({
          title: "Error",
          description: "Failed to load analysis data",
          variant: "destructive",
        });
        setLoading(false);
        return;
      }

      if (!sessionsData || sessionsData.length === 0) {
        setLoading(false);
        return;
      }

      const uniqueSubjects = Array.from(
        new Map(
          sessionsData.map((session) => [
            session.subjects.id,
            {
              id: session.subjects.id,
              name: session.subjects.name,
              grade: session.grade,
            },
          ])
        ).values()
      );
      setSubjects(uniqueSubjects);

      const analysisResults: any = {};
      for (const session of sessionsData) {
        const subjectId = session.subjects.id;
        const grade = session.grade;
        
        if (!analysisResults[subjectId]) {
          analysisResults[subjectId] = {
            name: session.subjects.name,
            grade,
            sessions: [],
            categories: {},
            overallScore: 0,
            totalQuestions: 0,
            correctAnswers: 0,
          };
        }

        const sessionEntry = {
          id: session.id,
          score: session.score,
          totalQuestions: session.total_questions,
          date: new Date(session.created_at).toLocaleDateString(),
          timeTaken: session.time_taken,
        };
        analysisResults[subjectId].sessions.push(sessionEntry);

        const { data: answersData, error: answersError } = await supabase
          .from("test_answers")
          .select(`
            id,
            question_id,
            is_correct,
            questions(
              id,
              category,
              category_id
            )
          `)
          .eq("test_session_id", session.id);

        if (answersError) {
          console.error("Error fetching test answers:", answersError);
          continue;
        }

        if (answersData) {
          const { data: categoriesData } = await supabase
            .from("subjects_categories")
            .select("id, name")
            .eq("subject_id", subjectId);

          const categoryMap = new Map();
          if (categoriesData) {
            categoriesData.forEach((cat) => {
              categoryMap.set(cat.id, cat.name);
            });
          }

          answersData.forEach((answer) => {
            const categoryId = answer.questions.category_id;
            const categoryName = categoryId && categoryMap.has(categoryId)
              ? categoryMap.get(categoryId)
              : answer.questions.category || "Unknown";
            
            const categoryKey = categoryId || categoryName;
            
            if (!analysisResults[subjectId].categories[categoryKey]) {
              analysisResults[subjectId].categories[categoryKey] = {
                name: categoryName,
                correct: 0,
                total: 0,
                questions: {},
              };
            }
            
            analysisResults[subjectId].categories[categoryKey].total += 1;
            if (answer.is_correct) {
              analysisResults[subjectId].categories[categoryKey].correct += 1;
            }
            
            if (!analysisResults[subjectId].categories[categoryKey].questions[answer.question_id]) {
              analysisResults[subjectId].categories[categoryKey].questions[answer.question_id] = {
                attempts: 0,
                correct: 0,
              };
            }
            
            analysisResults[subjectId].categories[categoryKey].questions[answer.question_id].attempts += 1;
            if (answer.is_correct) {
              analysisResults[subjectId].categories[categoryKey].questions[answer.question_id].correct += 1;
            }
          });

          analysisResults[subjectId].totalQuestions += answersData.length;
          analysisResults[subjectId].correctAnswers += answersData.filter((a) => a.is_correct).length;
        }
      }

      Object.keys(analysisResults).forEach((subjectId) => {
        const subject = analysisResults[subjectId];
        subject.overallScore = subject.totalQuestions > 0
          ? Math.round((subject.correctAnswers / subject.totalQuestions) * 100)
          : 0;
        
        Object.keys(subject.categories).forEach((categoryKey) => {
          const category = subject.categories[categoryKey];
          category.percentage = category.total > 0
            ? Math.round((category.correct / category.total) * 100)
            : 0;
        });
      });

      setAnalysisData(analysisResults);
    } catch (error) {
      console.error("Error in analysis:", error);
      toast({
        title: "Error",
        description: "Failed to process analysis data",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const generateSubjectData = (subjectId: string) => {
    if (!analysisData || !analysisData[subjectId]) return;

    const subject = analysisData[subjectId];
    
    const chartData = Object.keys(subject.categories).map((categoryKey) => {
      const category = subject.categories[categoryKey];
      return {
        name: category.name,
        score: category.percentage,
        correct: category.correct,
        total: category.total,
      };
    }).sort((a, b) => a.score - b.score);

    const strengths = [...chartData]
      .sort((a, b) => b.score - a.score)
      .slice(0, 2)
      .filter(category => category.score >= 70);

    const weaknesses = [...chartData]
      .sort((a, b) => a.score - b.score)
      .slice(0, 2)
      .filter(category => category.score < 70);

    const recentSessions = subject.sessions.slice(0, 5);

    const progressData = subject.sessions.slice(0, 10).reverse().map((session: any, index: number) => ({
      name: `Test ${index + 1}`,
      score: session.score,
      date: session.date,
    }));

    setSubjectData({
      name: subject.name,
      grade: subject.grade,
      overallScore: subject.overallScore,
      categoryData: chartData,
      strengths,
      weaknesses,
      recentSessions,
      progressData,
    });
  };

  // Calculate overall statistics across all subjects
  const overallStats = useMemo(() => {
    if (!analysisData) return null;
    
    let totalCorrect = 0;
    let totalQuestions = 0;
    let totalSessions = 0;
    let bestSubject = null;
    let bestScore = 0;
    let neededImprovementSubject = null;
    let lowestScore = 100;
    
    Object.entries(analysisData).forEach(([id, data]: [string, any]) => {
      totalCorrect += data.correctAnswers;
      totalQuestions += data.totalQuestions;
      totalSessions += data.sessions.length;
      
      if (data.overallScore > bestScore) {
        bestScore = data.overallScore;
        bestSubject = {
          name: data.name,
          grade: data.grade,
          score: data.overallScore
        };
      }
      
      if (data.overallScore < lowestScore && data.totalQuestions > 5) {
        lowestScore = data.overallScore;
        neededImprovementSubject = {
          name: data.name,
          grade: data.grade,
          score: data.overallScore
        };
      }
    });
    
    const overallScore = totalQuestions > 0 ? Math.round((totalCorrect / totalQuestions) * 100) : 0;
    
    return {
      totalQuestions,
      totalSessions,
      overallScore,
      bestSubject,
      neededImprovementSubject
    };
  }, [analysisData]);

  // Function to get color based on score
  const getScoreColor = (score: number) => {
    if (score >= 80) return "text-green-500";
    if (score >= 70) return "text-emerald-500";
    if (score >= 60) return "text-yellow-500";
    if (score >= 50) return "text-amber-500";
    return "text-red-500";
  };

  // Function to get badge variant based on score
  const getScoreBadgeVariant = (score: number): "default" | "outline" | "destructive" => {
    if (score >= 70) return "default";
    if (score >= 50) return "outline";
    return "destructive";
  };

  if (roleLoading || (role === 'parent' ? parentLoading : loading)) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center p-4">
          <div className="text-center">
            <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
            <p className="text-xl font-medium">Loading analysis data...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 p-4">
          <div className="container mx-auto max-w-4xl">
            <Card>
              <CardHeader>
                <CardTitle>Sign in Required</CardTitle>
                <CardDescription>
                  Please sign in to view your analysis.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button onClick={() => navigate("/auth")}>Sign In</Button>
              </CardContent>
            </Card>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (role === 'parent') {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 p-4">
          <div className="container mx-auto max-w-6xl">
            <div className="mb-8">
              <h1 className="text-3xl font-bold mb-2">Children's Performance Analysis</h1>
              <p className="text-gray-500">View your children's progress and insights</p>
            </div>
            {parentChildren.length > 0 ? (
              <div className="grid gap-6 md:grid-cols-2">
                {parentChildren.map((child) => (
                  <ChildPerformanceCard key={child.id} childName={child.student_name} childEmail={child.student_email} />
                ))}
              </div>
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle>No Children Found</CardTitle>
                  <CardDescription>Add children to view their performance analysis.</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button onClick={() => navigate('/parent/dashboard')}>Go to Parent Dashboard</Button>
                </CardContent>
              </Card>
            )}
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!analysisData || Object.keys(analysisData).length === 0) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 p-4">
          <div className="container mx-auto max-w-4xl">
            <Card>
              <CardHeader>
                <CardTitle>No Analysis Data Available</CardTitle>
                <CardDescription>
                  You haven't completed any tests yet. Take some tests to see your performance analysis.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button onClick={() => navigate("/dashboard")}>Go to Dashboard</Button>
              </CardContent>
            </Card>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 p-4">
        <div className="container mx-auto max-w-6xl">
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2">Performance Analysis</h1>
            <p className="text-gray-500">
              Track your progress and identify areas for improvement
            </p>
          </div>

          <Tabs value={selectedView} onValueChange={(value: string) => setSelectedView(value as "overview" | "subjects")} className="mb-6">
            <TabsList className="mb-4">
              <TabsTrigger value="overview">Overall Performance</TabsTrigger>
              <TabsTrigger value="subjects">Subject Analysis</TabsTrigger>
            </TabsList>
            
            <TabsContent value="overview">
              {overallStats && (
                <div className="space-y-6">
                  {/* Overall stats cards */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg">Overall Score</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="flex items-center justify-between">
                          <div className={`text-3xl font-bold ${getScoreColor(overallStats.overallScore)}`}>
                            {overallStats.overallScore}%
                          </div>
                          <BarChart2 className="h-8 w-8 text-muted-foreground" />
                        </div>
                        <Progress value={overallStats.overallScore} className="h-2 mt-2" />
                      </CardContent>
                    </Card>
                    
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg">Questions Answered</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="flex items-center justify-between">
                          <div className="text-3xl font-bold">{overallStats.totalQuestions}</div>
                          <BookOpen className="h-8 w-8 text-muted-foreground" />
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg">Tests Completed</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="flex items-center justify-between">
                          <div className="text-3xl font-bold">{overallStats.totalSessions}</div>
                          <Clock className="h-8 w-8 text-muted-foreground" />
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg">Achievement</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="flex items-center">
                          {overallStats.overallScore >= 80 ? (
                            <span className="text-3xl">🏆</span>
                          ) : overallStats.overallScore >= 70 ? (
                            <span className="text-3xl">🥇</span>
                          ) : overallStats.overallScore >= 60 ? (
                            <span className="text-3xl">🥈</span>
                          ) : overallStats.overallScore >= 50 ? (
                            <span className="text-3xl">🥉</span>
                          ) : (
                            <span className="text-3xl">🌱</span>
                          )}
                          <span className="ml-2 text-sm">
                            {overallStats.overallScore >= 80 ? "Expert" :
                             overallStats.overallScore >= 70 ? "Advanced" :
                             overallStats.overallScore >= 60 ? "Intermediate" :
                             overallStats.overallScore >= 50 ? "Developing" : "Beginner"}
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Subject comparison chart */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Subject Performance Comparison</CardTitle>
                      <CardDescription>How you're performing across different subjects</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="h-[300px] w-full">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={sortedSubjects}
                            margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis 
                              dataKey="name"
                              angle={-45}
                              textAnchor="end"
                              height={60}
                              tick={{ fontSize: 12 }}
                            />
                            <YAxis domain={[0, 100]} />
                            <Tooltip 
                              formatter={(value: number) => [`${value}%`, 'Score']}
                              labelFormatter={(name) => {
                                const subject = sortedSubjects.find(s => s.name === name);
                                return `${name} (Grade ${subject?.grade || '?'})`;
                              }}
                            />
                            <Bar dataKey="score" name="Score" barSize={40}>
                              {sortedSubjects.map((entry, index) => (
                                <Cell 
                                  key={`cell-${index}`} 
                                  fill={entry.score >= 70 ? '#10b981' : entry.score >= 50 ? '#f59e0b' : '#ef4444'} 
                                />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Strengths and Improvement Areas */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {overallStats.bestSubject && (
                      <Card>
                        <CardHeader className="bg-green-50 dark:bg-green-900/20">
                          <CardTitle className="flex items-center text-green-700 dark:text-green-400">
                            <Award className="mr-2 h-5 w-5" />
                            Your Strongest Subject
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-6">
                          <div className="flex justify-between items-center">
                            <div>
                              <p className="text-xl font-bold">{overallStats.bestSubject.name}</p>
                              <p className="text-sm text-muted-foreground">Grade {overallStats.bestSubject.grade}</p>
                            </div>
                            <Badge variant="default" className="text-lg py-2 px-3 bg-green-500">
                              {overallStats.bestSubject.score}%
                            </Badge>
                          </div>
                        </CardContent>
                        <CardFooter>
                          <Button 
                            variant="outline" 
                            className="w-full" 
                            onClick={() => {
                              setSelectedView("subjects");
                              setSelectedSubject(Object.keys(analysisData).find(
                                key => analysisData[key].name === overallStats.bestSubject.name && 
                                      analysisData[key].grade === overallStats.bestSubject.grade
                              ));
                            }}
                          >
                            View Details <ArrowRight className="ml-2 h-4 w-4" />
                          </Button>
                        </CardFooter>
                      </Card>
                    )}

                    {overallStats.neededImprovementSubject && (
                      <Card>
                        <CardHeader className="bg-amber-50 dark:bg-amber-900/20">
                          <CardTitle className="flex items-center text-amber-700 dark:text-amber-400">
                            <Flag className="mr-2 h-5 w-5" />
                            Area for Improvement
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-6">
                          <div className="flex justify-between items-center">
                            <div>
                              <p className="text-xl font-bold">{overallStats.neededImprovementSubject.name}</p>
                              <p className="text-sm text-muted-foreground">Grade {overallStats.neededImprovementSubject.grade}</p>
                            </div>
                            <Badge variant={getScoreBadgeVariant(overallStats.neededImprovementSubject.score)} className="text-lg py-2 px-3">
                              {overallStats.neededImprovementSubject.score}%
                            </Badge>
                          </div>
                        </CardContent>
                        <CardFooter>
                          <Button 
                            variant="outline" 
                            className="w-full" 
                            onClick={() => {
                              setSelectedView("subjects");
                              setSelectedSubject(Object.keys(analysisData).find(
                                key => analysisData[key].name === overallStats.neededImprovementSubject.name && 
                                      analysisData[key].grade === overallStats.neededImprovementSubject.grade
                              ));
                            }}
                          >
                            View Details <ArrowRight className="ml-2 h-4 w-4" />
                          </Button>
                        </CardFooter>
                      </Card>
                    )}
                  </div>
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="subjects">
              <div className="mb-6">
                <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                  <h2 className="text-2xl font-bold">Subject Analysis</h2>
                  
                  <div className="w-full sm:w-auto">
                    <Select value={selectedSubject || ""} onValueChange={setSelectedSubject}>
                      <SelectTrigger className="w-full sm:w-[250px]">
                        <SelectValue placeholder="Select a subject" />
                      </SelectTrigger>
                      <SelectContent>
                        {subjects.map((subject) => (
                          <SelectItem key={subject.id} value={subject.id}>
                            {subject.name} (Grade {subject.grade})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {subjectData && (
                <div className="space-y-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center justify-between">
                        <span>
                          {subjectData.name} - Grade {subjectData.grade}
                        </span>
                        <Badge variant={getScoreBadgeVariant(subjectData.overallScore)}>
                          {subjectData.overallScore}% Overall
                        </Badge>
                      </CardTitle>
                      <CardDescription>
                        Your overall performance in this subject
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Progress value={subjectData.overallScore} className="h-3 mb-2" />
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>Category Performance</CardTitle>
                      <CardDescription>
                        How you're performing in each category
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="h-[300px] w-full">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            layout="vertical"
                            data={subjectData.categoryData}
                            margin={{ top: 5, right: 30, left: 90, bottom: 5 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" domain={[0, 100]} unit="%" />
                            <YAxis 
                              type="category" 
                              dataKey="name" 
                              width={80}
                              tick={{ fontSize: 12 }}
                            />
                            <Tooltip 
                              formatter={(value: number) => [`${value}%`, 'Score']}
                              labelFormatter={(label) => `Category: ${label}`}
                            />
                            <Bar dataKey="score" name="Score" barSize={20}>
                              {subjectData.categoryData.map((entry: any, index: number) => (
                                <Cell 
                                  key={`cell-${index}`} 
                                  fill={entry.score >= 70 ? '#10b981' : entry.score >= 50 ? '#f59e0b' : '#ef4444'} 
                                />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Card>
                      <CardHeader className="bg-green-50 dark:bg-green-900/20">
                        <CardTitle className="flex items-center text-green-700 dark:text-green-400">
                          <Award className="mr-2 h-5 w-5" />
                          Strengths
                        </CardTitle>
                        <CardDescription>
                          Categories you excel in
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="pt-6">
                        {subjectData.strengths.length > 0 ? (
                          <ul className="space-y-4">
                            {subjectData.strengths.map((strength: any, index: number) => (
                              <li key={index} className="flex justify-between items-center">
                                <span className="font-medium">{strength.name}</span>
                                <div className="flex items-center">
                                  <span className="text-green-600 font-semibold mr-2">
                                    {strength.score}%
                                  </span>
                                  <span className="text-sm text-gray-500">
                                    ({strength.correct}/{strength.total})
                                  </span>
                                </div>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-gray-500 italic">
                            Keep practicing to develop strengths
                          </p>
                        )}
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="bg-red-50 dark:bg-red-900/20">
                        <CardTitle className="flex items-center text-red-700 dark:text-red-400">
                          <Flag className="mr-2 h-5 w-5" />
                          Areas to Improve
                        </CardTitle>
                        <CardDescription>
                          Categories that need more attention
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="pt-6">
                        {subjectData.weaknesses.length > 0 ? (
                          <ul className="space-y-4">
                            {subjectData.weaknesses.map((weakness: any, index: number) => (
                              <li key={index} className="flex justify-between items-center">
                                <span className="font-medium">{weakness.name}</span>
                                <div className="flex items-center">
                                  <span className="text-red-600 font-semibold mr-2">
                                    {weakness.score}%
                                  </span>
                                  <span className="text-sm text-gray-500">
                                    ({weakness.correct}/{weakness.total})
                                  </span>
                                </div>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-gray-500 italic">
                            Great job! No significant weak areas detected
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  </div>

                  <Card>
                    <CardHeader>
                      <CardTitle>Recent Test Sessions</CardTitle>
                      <CardDescription>
                        Your latest test attempts
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="overflow-x-auto">
                      <div className="min-w-[600px] md:min-w-0">
                        <table className="w-full border-collapse">
                          <thead>
                            <tr>
                              <th className="text-left py-2 px-3 border-b">Date</th>
                              <th className="text-center py-2 px-3 border-b">Score</th>
                              <th className="text-center py-2 px-3 border-b">Questions</th>
                              <th className="text-center py-2 px-3 border-b">Time Taken</th>
                              <th className="text-right py-2 px-3 border-b">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {subjectData.recentSessions.map((session: any, index: number) => (
                              <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                                <td className="py-2 px-3 border-b">{session.date}</td>
                                <td className="text-center py-2 px-3 border-b">
                                  <Badge variant={getScoreBadgeVariant(session.score)}>
                                    {session.score}%
                                  </Badge>
                                </td>
                                <td className="text-center py-2 px-3 border-b">{session.totalQuestions}</td>
                                <td className="text-center py-2 px-3 border-b">
                                  {Math.floor(session.timeTaken / 60)}m {session.timeTaken % 60}s
                                </td>
                                <td className="text-right py-2 px-3 border-b">
                                  <Button 
                                    variant="ghost" 
                                    size="sm"
                                    onClick={() => navigate(`/results/${session.id}`)}
                                  >
                                    View <ChevronRight className="ml-1 h-4 w-4" />
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>

                  {subjectData.progressData.length > 1 && (
                    <Card>
                      <CardHeader>
                        <CardTitle>Progress Over Time</CardTitle>
                        <CardDescription>
                          Your performance trend for recent tests
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="h-[250px]">
                          <ResponsiveContainer width="100%" height="100%">
                            <LineChart
                              data={subjectData.progressData}
                              margin={{ top: 20, right: 30, left: 20, bottom: 30 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="name" />
                              <YAxis domain={[0, 100]} />
                              <Tooltip
                                formatter={(value: number) => [`${value}%`, 'Score']}
                                labelFormatter={(index) => {
                                  if (index !== undefined && 
                                      subjectData.progressData && 
                                      subjectData.progressData[index] && 
                                      subjectData.progressData[index].date) {
                                    return subjectData.progressData[index].date;
                                  }
                                  return "N/A";
                                }}
                              />
                              <Line
                                type="monotone"
                                dataKey="score"
                                stroke="#8884d8"
                                strokeWidth={2}
                                activeDot={{ r: 8 }}
                              />
                            </LineChart>
                          </ResponsiveContainer>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Analysis;
